import type { CartItems } from '@/hooks/CartContext';
import type { ComboCartItemAllocation, ComboCartItemsMap, ComboRequirementGroup } from './comboCartUtils';
import { buildComboCartItemsMap, isCartItemMatchingEligibleSku, normalizeComboName } from './comboCartUtils';
import {
    buildComboAllocationId,
    buildComboInstanceId,
    getComboInstanceStatuses,
    parseComboInstanceId,
} from './comboCartInstances';

export interface ComboSectionAllocationHint {
    cartItemUid: string;
    cod_produto: number;
    qtd_embalagem: number;
    targetInstanceIds: string[];
    units?: number;
}

/** @deprecated Use ComboSectionAllocationHint */
export type ComboSectionDecrementHint = ComboSectionAllocationHint;

type PendingSectionAdjustment =
    | { type: 'decrement'; hint: ComboSectionAllocationHint }
    | { type: 'increment'; hint: ComboSectionAllocationHint };

let pendingSectionAdjustment: PendingSectionAdjustment | null = null;
let cachedAllocationMap: ComboCartItemsMap = {};
let cachedCartFingerprint = '';
let cachedComboFingerprint = '';
let cachedScopeKey = '';

const buildComboFingerprint = (
    combos: Array<{ id_campanha: string }>
): string => combos.map((combo) => combo.id_campanha).join('\0');

/** Item elegível a pelo menos um grupo de exigência de algum combo ativo. */
const isCartItemEligibleForAnyCombo = (
    item: Pick<CartItems, 'sku' | 'option_uid'>,
    combos: Array<{ grupos_exigencia?: ComboRequirementGroup[] }>
): boolean =>
    combos.some((combo) =>
        (combo.grupos_exigencia || []).some((grupo) =>
            (grupo.skus_elegiveis || []).some((sku) => isCartItemMatchingEligibleSku(item, sku))
        )
    );

export const setPendingComboSectionDecrement = (hint: ComboSectionAllocationHint | null): void => {
    pendingSectionAdjustment = hint ? { type: 'decrement', hint } : null;
};

export const setPendingComboSectionIncrement = (hint: ComboSectionAllocationHint | null): void => {
    pendingSectionAdjustment = hint ? { type: 'increment', hint } : null;
};

export const consumePendingComboSectionDecrement = (): ComboSectionAllocationHint | null => {
    const hint =
        pendingSectionAdjustment?.type === 'decrement' ? pendingSectionAdjustment.hint : null;
    pendingSectionAdjustment = null;
    return hint;
};

const consumePendingSectionAdjustment = (): PendingSectionAdjustment | null => {
    const adjustment = pendingSectionAdjustment;
    pendingSectionAdjustment = null;
    return adjustment;
};

/** Fingerprint só das linhas elegíveis a algum combo — produtos sem relação com nenhum combo
 *  não podem invalidar a alocação já calculada (senão adicionar um item qualquer ao carrinho
 *  descarta o estado combo/excedente vigente e recalcula do zero com o algoritmo greedy, que não
 *  é garantido a reproduzir a mesma distribuição por instância). */
const buildCartFingerprint = (
    cartItems: CartItems[],
    combos: Array<{ grupos_exigencia?: ComboRequirementGroup[] }>
): string =>
    cartItems
        .filter((item) => isCartItemEligibleForAnyCombo(item, combos))
        .map((item) => `${item.uid}:${item.quantity}:${item.sku}:${item.option_uid}`)
        .sort()
        .join('|');

const cloneAllocationMap = (map: ComboCartItemsMap): ComboCartItemsMap => {
    const cloned: ComboCartItemsMap = {};

    Object.entries(map).forEach(([uid, allocations]) => {
        cloned[uid] = allocations.map((entry) => ({ ...entry }));
    });

    return cloned;
};

const getGrupoId = (grupo: ComboRequirementGroup): string => {
    const raw = grupo as unknown as Record<string, unknown>;
    const candidates = [raw.id, raw.id_grupo, raw.desc_grupo, raw.Desc_grupo];

    for (const value of candidates) {
        if (value != null && String(value).trim()) {
            return String(value).trim();
        }
    }

    return '';
};

const matchesProductPackaging = (
    entry: ComboCartItemAllocation,
    cod_produto: number,
    qtd_embalagem: number
) =>
    Number(entry.cod_produto) === Number(cod_produto) &&
    Number(entry.qtd_embalagem) === Number(qtd_embalagem);

const allocationBelongsToGrupo = (
    entry: ComboCartItemAllocation,
    grupo: ComboRequirementGroup
): boolean =>
    (grupo.skus_elegiveis || []).some(
        (sku) =>
            Number(entry.cod_produto) === Number(sku.cod_produto) &&
            Number(entry.qtd_embalagem) === Number(sku.qtd_embalagem)
    );

const sortByKitIndexDesc = (instanceIds: string[]) =>
    [...instanceIds].sort((a, b) => {
        const kb = parseComboInstanceId(b)?.kitIndex ?? 0;
        const ka = parseComboInstanceId(a)?.kitIndex ?? 0;
        return kb - ka;
    });

const sortByKitIndexAsc = (instanceIds: string[]) =>
    [...instanceIds].sort((a, b) => {
        const ka = parseComboInstanceId(a)?.kitIndex ?? 0;
        const kb = parseComboInstanceId(b)?.kitIndex ?? 0;
        return ka - kb;
    });

const getGroupAllocatedQuantityInMap = (
    map: ComboCartItemsMap,
    idCampanha: string,
    comboInstanceId: string,
    grupo: ComboRequirementGroup
): number => {
    let total = 0;

    Object.values(map).forEach((lineAllocations) => {
        lineAllocations.forEach((entry) => {
            if (entry.id_campanha !== idCampanha) return;
            if (entry.comboInstanceId !== comboInstanceId) return;
            if (!allocationBelongsToGrupo(entry, grupo)) return;
            total += entry.allocatedPackaging;
        });
    });

    return total;
};

const getComboInstanceIdsInMap = (map: ComboCartItemsMap, idCampanha: string): string[] => {
    const ids = new Set<string>();

    Object.values(map).forEach((lineAllocations) => {
        lineAllocations.forEach((entry) => {
            if (entry.id_campanha === idCampanha && entry.comboInstanceId) {
                ids.add(entry.comboInstanceId);
            }
        });
    });

    return sortByKitIndexAsc([...ids]);
};

const findComboAndGrupoForHint = (
    combos: Array<{
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        nome_campanha?: string;
    }>,
    hint: ComboSectionAllocationHint
) => {
    for (const combo of combos) {
        for (const grupo of combo.grupos_exigencia || []) {
            const matches = (grupo.skus_elegiveis || []).some(
                (sku) =>
                    Number(sku.cod_produto) === Number(hint.cod_produto) &&
                    Number(sku.qtd_embalagem) === Number(hint.qtd_embalagem)
            );
            if (matches) return { combo, grupo };
        }
    }

    return null;
};

const resolveIncrementTargetInstanceId = (
    map: ComboCartItemsMap,
    hint: ComboSectionAllocationHint,
    idCampanha: string,
    grupo: ComboRequirementGroup
): string => {
    const required = grupo.quantidade_base_por_kit ?? 0;
    const targetIds = hint.targetInstanceIds.filter(Boolean);

    for (const instanceId of targetIds) {
        const current = getGroupAllocatedQuantityInMap(map, idCampanha, instanceId, grupo);
        if (current < required) return instanceId;
    }

    const minTargetKit = Math.min(
        ...targetIds.map((instanceId) => parseComboInstanceId(instanceId)?.kitIndex ?? 1)
    );
    const existingInstances = getComboInstanceIdsInMap(map, idCampanha);

    for (const instanceId of existingInstances) {
        const kitIndex = parseComboInstanceId(instanceId)?.kitIndex ?? 0;
        if (kitIndex <= minTargetKit) continue;

        const current = getGroupAllocatedQuantityInMap(map, idCampanha, instanceId, grupo);
        if (current < required) return instanceId;
    }

    const maxKitIndex = existingInstances.reduce((max, instanceId) => {
        const kitIndex = parseComboInstanceId(instanceId)?.kitIndex ?? 0;
        return Math.max(max, kitIndex);
    }, 0);

    return buildComboInstanceId(idCampanha, maxKitIndex + 1);
};

const addOneUnitToInstance = (
    map: ComboCartItemsMap,
    hint: ComboSectionAllocationHint,
    comboInstanceId: string,
    combo: { id_campanha: string; nome_campanha?: string },
    grupo: ComboRequirementGroup
) => {
    const grupoId = getGrupoId(grupo);
    const kitIndex = parseComboInstanceId(comboInstanceId)?.kitIndex ?? 1;
    const lineAllocations = map[hint.cartItemUid] ?? [];

    const existing = lineAllocations.find(
        (entry) =>
            entry.id_campanha === combo.id_campanha &&
            entry.comboInstanceId === comboInstanceId &&
            entry.grupoId === grupoId &&
            matchesProductPackaging(entry, hint.cod_produto, hint.qtd_embalagem)
    );

    if (existing) {
        existing.allocatedPackaging += 1;
        map[hint.cartItemUid] = lineAllocations;
        return;
    }

    lineAllocations.push({
        id_campanha: combo.id_campanha,
        nome_campanha: normalizeComboName(combo),
        comboInstanceId,
        kitIndex,
        grupoId,
        allocationId: buildComboAllocationId({
            cartItemId: hint.cartItemUid,
            comboId: combo.id_campanha,
            comboInstanceId,
            groupId: grupoId,
            cod_produto: hint.cod_produto,
            qtd_embalagem: hint.qtd_embalagem,
        }),
        allocatedPackaging: 1,
        cod_produto: hint.cod_produto,
        qtd_embalagem: hint.qtd_embalagem,
        appliesDiscount: false,
    });

    map[hint.cartItemUid] = lineAllocations;
};

/** Remove embalagens da inst�ncia alvo, preservando as demais inst�ncias. */
export const applySectionDecrementToAllocationMap = (
    previousMap: ComboCartItemsMap,
    hint: ComboSectionAllocationHint
): ComboCartItemsMap => {
    const nextMap = cloneAllocationMap(previousMap);
    const lineAllocations = nextMap[hint.cartItemUid] ?? [];
    let remaining = hint.units ?? 1;

    const targetIds = new Set(hint.targetInstanceIds.filter(Boolean));

    sortByKitIndexDesc(hint.targetInstanceIds).forEach((instanceId) => {
        if (remaining <= 0) return;

        lineAllocations.forEach((entry) => {
            if (remaining <= 0) return;
            if (!targetIds.has(entry.comboInstanceId ?? '')) return;
            if (!matchesProductPackaging(entry, hint.cod_produto, hint.qtd_embalagem)) return;

            const removable = Math.min(entry.allocatedPackaging, remaining);
            if (removable <= 0) return;

            entry.allocatedPackaging -= removable;
            remaining -= removable;
        });
    });

    nextMap[hint.cartItemUid] = lineAllocations.filter((entry) => entry.allocatedPackaging > 0);

    return nextMap;
};

/**
 * Ap�s remover unidades de um combo completo, preenche as vagas com o mesmo produto
 * alocado em kits posteriores (em andamento) ou com quantidade excedente da linha.
 */
export const rebalanceComboSlotsAfterSectionDecrement = (
    map: ComboCartItemsMap,
    hint: ComboSectionAllocationHint,
    combos: Array<{
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        nome_campanha?: string;
    }>,
    cartItems: CartItems[]
): ComboCartItemsMap => {
    const match = findComboAndGrupoForHint(combos, hint);
    if (!match) return map;

    const { combo, grupo } = match;
    const nextMap = cloneAllocationMap(map);
    const required = grupo.quantidade_base_por_kit ?? 0;
    const targetInstances = sortByKitIndexAsc(hint.targetInstanceIds.filter(Boolean));

    const getLineAllocatedTotal = (uid: string) =>
        (nextMap[uid] ?? []).reduce((sum, entry) => sum + entry.allocatedPackaging, 0);

    const getCartLineQuantity = (uid: string) =>
        cartItems.find((item) => item.uid === uid)?.quantity ?? 0;

    const tryPullFromHigherInstances = (targetInstanceId: string): boolean => {
        const targetKitIndex = parseComboInstanceId(targetInstanceId)?.kitIndex ?? 0;
        const lineAllocations = nextMap[hint.cartItemUid] ?? [];

        const sourceEntry = lineAllocations
            .filter(
                (entry) =>
                    entry.id_campanha === combo.id_campanha &&
                    matchesProductPackaging(entry, hint.cod_produto, hint.qtd_embalagem) &&
                    (parseComboInstanceId(entry.comboInstanceId ?? '')?.kitIndex ?? 0) > targetKitIndex &&
                    entry.allocatedPackaging > 0
            )
            .sort(
                (a, b) =>
                    (parseComboInstanceId(a.comboInstanceId ?? '')?.kitIndex ?? 0) -
                    (parseComboInstanceId(b.comboInstanceId ?? '')?.kitIndex ?? 0)
            )[0];

        if (!sourceEntry) return false;

        sourceEntry.allocatedPackaging -= 1;
        nextMap[hint.cartItemUid] = lineAllocations.filter((entry) => entry.allocatedPackaging > 0);

        addOneUnitToInstance(nextMap, hint, targetInstanceId, combo, grupo);
        return true;
    };

    const tryFillFromExcess = (targetInstanceId: string): boolean => {
        const currentGrupo = getGroupAllocatedQuantityInMap(
            nextMap,
            combo.id_campanha,
            targetInstanceId,
            grupo
        );
        if (currentGrupo >= required) return false;

        const excess = getCartLineQuantity(hint.cartItemUid) - getLineAllocatedTotal(hint.cartItemUid);
        if (excess <= 0) return false;

        addOneUnitToInstance(nextMap, hint, targetInstanceId, combo, grupo);
        return true;
    };

    targetInstances.forEach((targetInstanceId) => {
        let openSlots =
            required -
            getGroupAllocatedQuantityInMap(nextMap, combo.id_campanha, targetInstanceId, grupo);

        while (openSlots > 0) {
            if (tryPullFromHigherInstances(targetInstanceId)) {
                openSlots -= 1;
                continue;
            }

            if (tryFillFromExcess(targetInstanceId)) {
                openSlots -= 1;
                continue;
            }

            break;
        }
    });

    Object.keys(nextMap).forEach((uid) => {
        nextMap[uid] = (nextMap[uid] ?? []).filter((entry) => entry.allocatedPackaging > 0);
    });

    return nextMap;
};

/** Adiciona embalagens na inst�ncia correta sem realocar outros produtos do kit. */
export const applySectionIncrementToAllocationMap = (
    previousMap: ComboCartItemsMap,
    hint: ComboSectionAllocationHint,
    combos: Array<{
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        nome_campanha?: string;
    }>
): ComboCartItemsMap => {
    const match = findComboAndGrupoForHint(combos, hint);
    if (!match) return previousMap;

    const { combo, grupo } = match;
    const nextMap = cloneAllocationMap(previousMap);
    const units = hint.units ?? 1;

    for (let index = 0; index < units; index += 1) {
        const targetInstanceId = resolveIncrementTargetInstanceId(
            nextMap,
            hint,
            combo.id_campanha,
            grupo
        );
        addOneUnitToInstance(nextMap, hint, targetInstanceId, combo, grupo);
    }

    return nextMap;
};

const getComboBenefitRules = (combo: { regras_beneficio?: unknown }) => {
    const regras = combo?.regras_beneficio;
    return Array.isArray(regras) ? regras : [];
};

const getComboMaxKitLimit = (combo: { regras_beneficio?: unknown }): number | null => {
    const regra = getComboBenefitRules(combo)[0] as { max_combo?: number | null } | undefined;
    if (regra?.max_combo == null) return null;
    const limit = Number(regra.max_combo);
    return Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : null;
};

/** Recalcula appliesDiscount ap�s ajuste manual de aloca��es. */
export const recalculateAppliesDiscountInMap = (
    combos: Array<{
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        regras_beneficio?: unknown;
    }>,
    map: ComboCartItemsMap,
    cartItems: CartItems[]
): ComboCartItemsMap => {
    const nextMap = cloneAllocationMap(map);

    combos.forEach((combo) => {
        const statuses = getComboInstanceStatuses(combo, cartItems, (uid) => nextMap[uid]);
        const maxDiscountKits = getComboMaxKitLimit(combo) ?? Number.POSITIVE_INFINITY;
        let discountableCompleteCount = 0;

        statuses
            .sort(
                (a, b) =>
                    (parseComboInstanceId(a.comboInstanceId)?.kitIndex ?? 0) -
                    (parseComboInstanceId(b.comboInstanceId)?.kitIndex ?? 0)
            )
            .forEach((status) => {
                const appliesDiscount = status.isComplete && discountableCompleteCount < maxDiscountKits;

                if (status.isComplete && appliesDiscount) {
                    discountableCompleteCount += 1;
                }

                Object.values(nextMap).forEach((lineAllocations) => {
                    lineAllocations.forEach((entry) => {
                        if (
                            entry.id_campanha !== combo.id_campanha ||
                            entry.comboInstanceId !== status.comboInstanceId
                        ) {
                            return;
                        }
                        entry.appliesDiscount = appliesDiscount;
                    });
                });
            });
    });

    return nextMap;
};

/** Cache compartilhado entre todas as instâncias de useComboCartItems.
 *  `cartScopeKey` identifica o carrinho ativo (ex.: cliente/bandeira) — sem isso, o cache
 *  poderia reter a alocação de um carrinho diferente do exibido após uma troca de cliente. */
export const buildComboCartItemsMapWithSectionDecrement = (
    cartItems: CartItems[],
    combos: Array<{
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        regras_beneficio?: unknown;
        nome_campanha?: string;
    }>,
    cartScopeKey = ''
): ComboCartItemsMap => {
    const fingerprint = buildCartFingerprint(cartItems, combos);
    const comboFingerprint = buildComboFingerprint(combos);
    const adjustment = consumePendingSectionAdjustment();
    const sameScope = cartScopeKey === cachedScopeKey;
    const baseMap = buildComboCartItemsMap(cartItems, combos);

    if (adjustment) {
        const sourceMap =
            sameScope && Object.keys(cachedAllocationMap).length > 0 ? cachedAllocationMap : baseMap;

        const adjusted =
            adjustment.type === 'decrement'
                ? rebalanceComboSlotsAfterSectionDecrement(
                      applySectionDecrementToAllocationMap(sourceMap, adjustment.hint),
                      adjustment.hint,
                      combos,
                      cartItems
                  )
                : applySectionIncrementToAllocationMap(sourceMap, adjustment.hint, combos);

        cachedAllocationMap = recalculateAppliesDiscountInMap(combos, adjusted, cartItems);
        cachedCartFingerprint = fingerprint;
        cachedComboFingerprint = comboFingerprint;
        cachedScopeKey = cartScopeKey;
        return cachedAllocationMap;
    }

    if (
        sameScope &&
        fingerprint === cachedCartFingerprint &&
        comboFingerprint === cachedComboFingerprint &&
        Object.keys(cachedAllocationMap).length > 0
    ) {
        return cachedAllocationMap;
    }

    cachedAllocationMap = baseMap;
    cachedCartFingerprint = fingerprint;
    cachedComboFingerprint = comboFingerprint;
    cachedScopeKey = cartScopeKey;
    return cachedAllocationMap;
};

export const resolveComboSectionTargetInstanceIds = (item: {
    comboInstanceId?: string;
    mergedInstanceIds?: string[];
}): string[] => {
    if (item.mergedInstanceIds?.length) {
        return item.mergedInstanceIds;
    }
    if (item.comboInstanceId) {
        return [item.comboInstanceId];
    }
    return [];
};
